/**
 * Domain types. These mirror the API contracts in SPECIFICATION.md §9, not the
 * Table Storage entities in §8 — the API never exposes table rows directly.
 */

export const LANGUAGES = ['en', 'ru', 'uz'] as const
export type UiLanguage = (typeof LANGUAGES)[number]

export const CONTENT_LANGUAGES = ['en', 'ru'] as const
export type ContentLanguage = (typeof CONTENT_LANGUAGES)[number]

export const LEVELS = ['foundation', 'undergraduate', 'intermediate', 'advanced', 'graduate'] as const
export type Level = (typeof LEVELS)[number]

export const FORMATS = ['pdf', 'epub', 'zip'] as const
export type BookFormat = (typeof FORMATS)[number]

export const AVAILABILITIES = ['hosted', 'external'] as const
export type Availability = (typeof AVAILABILITIES)[number]

export type AccessType = 'open-access' | 'public-domain' | 'free-of-charge' | 'mixed'

export type Relation = 'primary' | 'related'

/** The two rating pools (§FR-6.3). There is no combined pool, anywhere. */
export type Audience = 'teacher' | 'general'

export type Role = 'user' | 'teacher' | 'admin'

export type ReviewStatus = 'published' | 'hidden' | 'removed'

/**
 * One book as it appears in the slim catalogue (§9.1 `GET /catalog`) — what a
 * card, a filter or the search index needs, and nothing more.
 *
 * The detail-only fields live in `BookDetails` and arrive separately: inlining
 * them puts the catalogue at 117 KB gzipped against the 60 KB budget in §15.5,
 * and descriptions alone account for 35 KB of that.
 */
export interface Book {
  id: string
  title: string
  authors: string[]
  yearPublished?: number | null
  language: ContentLanguage
  publisher?: string | null
  level: Level
  keywords: string[]
  collection: string
  availability: Availability
  format: BookFormat
  sizeBytes: number
  status: 'active' | 'archived'
  createdAt: string
  /** Discipline links, split by relation, as ids into `Subject`. */
  subjects: { primary: string[]; related: string[] }
}

/**
 * The rest of a book's record: everything the book page shows and the search
 * index weights at the bottom. Served per title by `GET /books/{bookId}` and,
 * for the index, as one cacheable document alongside the catalogue.
 */
export interface BookDetails {
  description?: string | null
  edition?: string | null
  originalYear?: number | null
  sha256?: string | null
  fileName?: string | null
  sourceSite?: string | null
  sourcePageUrl?: string | null
  retrievedAt?: string | null
  licenseName?: string | null
  licenseUrl?: string | null
  usageRestrictions?: string | null
  accessType?: AccessType
}

/** A book with its details merged, as the book page consumes it. */
export type FullBook = Book & BookDetails

export interface Subject {
  id: string
  nameEn: string
  nameRu?: string | null
  nameUz?: string | null
  descriptionEn?: string | null
  descriptionRu?: string | null
  descriptionUz?: string | null
  sortOrder: number
  bookCount: number
  primaryCount: number
  relatedCount: number
  status: 'active' | 'archived'
}

/** The cacheable document the whole client-side index is built from (§15.3). */
export interface Catalog {
  version: number
  generatedAt: string
  subjects: Subject[]
  books: Book[]
}

/** Per-pool aggregate. Averages are derived, never stored (§8.4). */
export interface PoolStats {
  ratingSum: number
  ratingCount: number
  reviewCount: number
  /** Counts for ratings 1..5, in that order. */
  histogram: [number, number, number, number, number]
}

export interface BookStats {
  bookId: string
  downloadsTotal: number
  downloadsUnique: number
  externalOpens: number
  externalOpensUnique: number
  manualAdjustment: number
  teacher: PoolStats
  general: PoolStats
  lastDownloadAt?: string | null
}

export interface Review {
  bookId: string
  userOid: string
  rating: number
  body?: string | null
  audience: Audience
  authorDisplayName: string
  authorRoles: Role[]
  contentLanguage?: string | null
  status: ReviewStatus
  reportCount: number
  createdAt: string
  updatedAt: string
  isEdited: boolean
  moderationReason?: string | null
}

/**
 * What a review write returns (§16.3 step 8): the saved review plus both
 * refreshed pools, so the two rating summaries settle without a second request.
 * Never a combined figure — there is no overall rating in this product (FR-6.6).
 */
export interface ReviewWriteResult {
  review: Review
  teacher: PoolStats
  general: PoolStats
}

export interface UserProfile {
  oid: string
  email: string
  displayName: string
  roles: Role[]
  preferredLanguage: UiLanguage
  isBlocked: boolean
  blockedReason?: string | null
  createdAt: string
}

export interface DownloadRecord {
  bookId: string
  firstDownloadAt: string
  lastDownloadAt: string
  userDownloadCount: number
  kind: 'download' | 'external'
}

/** `POST /books/{id}/download` returns one of these two shapes (§9.1). */
export type DownloadTicket =
  | { kind: 'sas'; url: string; expiresAt: string; fileName: string; sizeBytes: number }
  | { kind: 'external'; url: string; publisher?: string | null }

/** RFC 9457 problem document (§9). */
export interface ProblemDetails {
  type: string
  title: string
  status: number
  detail?: string
  instance?: string
  traceId?: string
}

export interface Page<T> {
  items: T[]
  page: number
  pageSize: number
  totalCount: number
  hasMore: boolean
}

/** Ratings are integers 1–5 (§FR-6.1). */
export type Rating = 1 | 2 | 3 | 4 | 5

export const COLLECTIONS = [
  'ai',
  'algorithms',
  'appsec',
  'business',
  'cloud',
  'crypto',
  'csharp',
  'databases',
  'humanities',
  'java',
  'mathematics',
  'python',
  'security',
  'swe',
  'systems',
  'web',
] as const
export type Collection = (typeof COLLECTIONS)[number]

/** The catalogue's publication-year span, used to bound the year slider. */
export const YEAR_MIN = 1992
export const YEAR_MAX = 2026
